import React from 'react'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import { Button, Dropdown } from 'antd'
import { useGetUserTestZones } from '../hooks/TestZones'
import { useUserPlant } from '../hooks/User'
import { DownloadOutlined } from '@ant-design/icons'
import { useUserPlantInfo } from '../hooks/Plants'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import objectSupport from 'dayjs/plugin/objectSupport'
import { dispatchEvent } from '../CloudWatch-init'
import { apiGetFaults } from '../api/faults'

dayjs.extend(objectSupport)
dayjs.extend(utc)
dayjs.extend(timezone)

const TYPE_FIELDS = {
  component: 'component_id',
  harness: 'harness',
  device: 'device_name',
}

const TYPE_DESCRIPTION = {
  component: 'component_description',
  harness: 'description',
  device: 'device_name',
}

const createExportData = ({ faults, intl, testZones, timezone }) => {
  return faults.map((fault) => {
    const userId = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.userId',
    })
    const date = intl.formatMessage({ id: 'faultReport.cvsExportHeaders.date' })
    const vin = intl.formatMessage({ id: 'faultReport.cvsExportHeaders.vin' })
    const sequenceNumber = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.sequenceNumber',
      defaultMessage: 'Sequence Number',
    })
    const makeModel = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.makeModel',
      defaultMessage: 'Make Model',
    })
    const type = intl.formatMessage({ id: 'faultReport.cvsExportHeaders.type' })
    const reason = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.reason',
    })
    const annotation = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.annotation',
    })
    const station = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.station',
    })
    const vinStatus = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.vinStatus',
      defaultMessage: 'Vin Status',
    })
    const dtcs = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.dtcs',
      defaultMessage: 'DTCs',
    })
    const stationAlert = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.stationAlert',
    })
    const reportType = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.reportType',
    })
    const hardwareLocationId = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.hardwareLocationId',
      defaultMessage: 'Hardware Location ID',
    })
    const testZoneDesc = intl.formatMessage({
      id: 'faultReport.cvsExportHeaders.testZoneDesc',
      defaultMessage: 'Test Zone',
    })
    let data = {}
    data[userId] = fault.created_by
    data[date] = dayjs(fault.created_at)
      .tz(timezone)
      .format('YYYY-MM-DD HH:mm:ss')
    data[vin] = fault.vin
    data[sequenceNumber] = fault.sequence_number
    data[makeModel] = fault.model_description
    data[type] = fault.type
    data[reason] = fault.reason
    data[annotation] = fault.note
    data[station] = fault.station
    data[vinStatus] = fault.vin_status
    data[dtcs] = fault.DTCs
      ? fault.DTCs.map((d) => `${d.dtc} - ${d.short_desc}`).join(', ')
      : ''
    data[stationAlert] = fault.station_alert
    data[reportType] =
      fault.type == 'other'
        ? fault.device_name
        : fault[TYPE_FIELDS[fault.type]] +
          ' : ' +
          fault[TYPE_DESCRIPTION[fault.type]]
    data[hardwareLocationId] = fault.hardware_location_id
    data[testZoneDesc] = testZones[fault.hardware_location_id]
    return data
  })
}

const ExportButton = ({
  faults,
  intl,
  isMonitor = false,
  searchTerm,
  previousShiftTimes,
}) => {
  const { allTestZones } = useGetUserTestZones()
  const [userPlant] = useUserPlant()
  const { plantInfo } = useUserPlantInfo()
  const [isLoadingPrevShift, setIsLoadingPrevShift] = React.useState(false)

  const [testZonesByLocationId, setTestZonesByLocationId] = React.useState({})

  React.useEffect(() => {
    if (!allTestZones || allTestZones.length === 0 || !userPlant) {
      return
    }
    const tzs = {}
    allTestZones.forEach((tz) => {
      if (tz.plant.name.toLowerCase() === userPlant.toLowerCase()) {
        tzs[tz.specifier] = tz.description
      }
    })
    setTestZonesByLocationId(tzs)
  }, [allTestZones, userPlant])

  const sendDownloadEvent = (recordCount) => {
    if (isMonitor) {
      dispatchEvent('MONITOR_PAGE_DOWNLOADED', {
        plant: userPlant,
        timestamp: Date.now(),
        recordCount,
      })
    }
  }

  const doExport = (faultsToExport) => {
    const data = createExportData({
      faults: faultsToExport,
      intl,
      testZones: testZonesByLocationId,
      timezone: plantInfo?.timezone || 'UTC',
    })
    sendDownloadEvent(faultsToExport.length)
    const dataString = Papa.unparse(data)
    const blob = new Blob([dataString], { type: 'text/csv;charset=utf-8' })
    saveAs(blob, 'faults-report.csv')
  }

  const onExportCurrentShift = () => {
    doExport(faults)
  }

  const onExportPreviousShift = async () => {
    if (!searchTerm || !previousShiftTimes) return

    const rawAlerts = localStorage.getItem('station-alert')
    const stationAlerts = rawAlerts ? JSON.parse(rawAlerts) : 'send,skip'

    setIsLoadingPrevShift(true)
    try {
      const result = await apiGetFaults({
        makeModelIds: searchTerm.makeModelIds || [],
        stationIds: searchTerm.stationIds || [],
        start_at: previousShiftTimes.start_at,
        end_at: previousShiftTimes.end_at,
        station_alerts: stationAlerts,
        plants: userPlant || '',
        zones: searchTerm.zones || [],
        teams: searchTerm.teams || [],
        department: searchTerm.department || null,
        testZones: [],
        reason: searchTerm.reason || null,
        vinStatus: searchTerm.vinStatus || null,
      })
      const prevFaults = result.data || result || []
      doExport(Array.isArray(prevFaults) ? prevFaults : [])
    } catch (e) {
      console.error('Failed to fetch previous shift faults', e)
    } finally {
      setIsLoadingPrevShift(false)
    }
  }

  if (isMonitor) {
    const menuItems = [
      {
        key: 'current',
        label: intl.formatMessage({
          id: 'faultReport.exportText.currentShift',
          defaultMessage: 'Current Shift',
        }),
        onClick: onExportCurrentShift,
      },
      {
        key: 'previous',
        label: intl.formatMessage({
          id: 'faultReport.exportText.previousShift',
          defaultMessage: 'Previous Shift',
        }),
        onClick: onExportPreviousShift,
      },
    ]

    return (
      <Dropdown
        menu={{ items: menuItems }}
        disabled={
          isLoadingPrevShift || !Array.isArray(faults) || faults?.length === 0
        }
      >
        <Button
          icon={<DownloadOutlined />}
          type="primary"
          size="small"
          loading={isLoadingPrevShift}
        >
          {intl.formatMessage({ id: 'faultReport.exportText.exportLabel' })}
        </Button>
      </Dropdown>
    )
  }

  return (
    <Button
      disabled={!Array.isArray(faults) || faults?.length === 0}
      onClick={onExportCurrentShift}
      icon={<DownloadOutlined />}
      type="primary"
      size="small"
    >
      {intl.formatMessage({ id: 'faultReport.exportText.exportLabel' })}
    </Button>
  )
}

export default ExportButton
